import React, { useCallback, useEffect, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useSelector } from 'react-redux'
import { Checkbox, Row } from 'antd'
import { differenceWith, isEmpty, isNil, join, map, size } from 'lodash'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'
import { showErrorNotification } from '../../../core/helpers'
import { usePrevious, useQueryParams } from '../../../core/hooks'
import { areSameValue } from '../../../core/utils'

import { StyledHotkeyContainer } from '../../../common/components/styled'
import { COMMON_AVAILABLE_HOTKEYS, PAGINATION } from '../../../common/constants'

import { useLoggedInUser } from '../../../account/hooks'
import { useUpdateLocationCuisineTypeAndLocationTypeSameAsBrandMutation } from '../../../brand-curation-tasks/hooks'
import { brandCurationTaskPropType } from '../../../brand-curation-tasks/prop-types'
import {
  useBulkUpdateLocationsMutation,
  useLocationsQuery,
  useLocationTableHotkeyHighlights,
} from '../../hooks'
import useSelectedItemIds from '../../hooks/use-selected-item-ids'
import { createLocationTableColumns } from '../../services'
import { StyledLocationsTable } from '../styled'

import LocationEditableRow from './location-editable-row'
import LocationsHotkeysLegend from './locations-hotkeys-legend'
import LocationsTableFooter from './locations-table-footer'

const LocationsTable = ({ brandCurationTask, isDisabled }) => {
  const { settings: userSettings } = useLoggedInUser()

  const {
    currentPage = PAGINATION.DEFAULT_PAGE,
    pageSize = userSettings?.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    setQueryParams,
  } = useQueryParams()

  useLocationTableHotkeyHighlights(isDisabled, brandCurationTask.isCurationDone)

  const {
    mainLocationTypeOptions,
    additionalLocationTypeOptions,
    cuisineTypeOptions,
    awardOptions,
  } = useSelector(store => store.common.metadata.data)

  const { selectedRowKey, isHotkeyEditModeEnabled } = useSelector(
    store => store.locations.locations.hotkeys,
  )

  const { filters, pagination } = useMemo(
    () => ({
      filters: {
        brandId: brandCurationTask.brand.id,
      },
      pagination: {
        pageSize,
        currentPage,
      },
    }),
    [brandCurationTask.brand.id, pageSize, currentPage],
  )

  const {
    data,
    isLoading,
    error,
    refetch: refetchLocations,
  } = useLocationsQuery(filters, pagination)
  const { result, total } = data || {}

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Location error',
        description: error,
      })
    }
  }, [error])

  const [updateCuisineTypeAndLocationTypeSameAsBrand] =
    useUpdateLocationCuisineTypeAndLocationTypeSameAsBrandMutation()

  const handleChangePagination = useCallback(
    (page, size) =>
      setQueryParams({
        currentPage: size !== pageSize ? PAGINATION.DEFAULT_PAGE : page,
        pageSize: size,
      }),
    [pageSize, setQueryParams],
  )

  const allItemIds = useMemo(() => map(result, 'id'), [result])

  const {
    selectedItemIds,
    unselectAllItemIds,
    toggleSelectItemId,
    toggleSelectAllItemIds,
  } = useSelectedItemIds(allItemIds)

  const [bulkUpdateLocations] = useBulkUpdateLocationsMutation()

  const isEditDisabled = useMemo(
    () => size(selectedItemIds) <= 1 || isDisabled,
    [isDisabled, selectedItemIds],
  )

  const paginationConfig = useMemo(() => {
    if (isNil(total)) {
      return false
    }

    return {
      current: Number(currentPage),
      pageSize,
      total,
      onChange: handleChangePagination,
      showSizeChanger: true,
      onShowSizeChange: handleChangePagination,
      pageSizeOptions: PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS,
    }
  }, [total, currentPage, pageSize, handleChangePagination])

  const tableColumns = useMemo(
    () =>
      createLocationTableColumns({
        isDisabled,
        isTaskCurationDone: brandCurationTask.isCurationDone,
        isTaskQaDone: brandCurationTask.isQaDone,
        cuisineTypeOptions,

        mainLocationTypeOptions,
        additionalLocationTypeOptions,
        awardOptions,
        result,
        areHotkeysEnabled: !isHotkeyEditModeEnabled,
        focusedItemId: selectedRowKey,
        selectedItemIds,
        toggleSelectItemId,
        toggleSelectAllItemIds,
      }),
    [
      isDisabled,
      brandCurationTask.isCurationDone,
      brandCurationTask.isQaDone,
      cuisineTypeOptions,
      mainLocationTypeOptions,
      additionalLocationTypeOptions,
      awardOptions,
      result,
      selectedItemIds,
      toggleSelectItemId,
      toggleSelectAllItemIds,
      selectedRowKey,
      isHotkeyEditModeEnabled,
    ],
  )

  const tableComponents = useMemo(
    () => ({
      body: {
        row: LocationEditableRow,
      },
    }),
    [],
  )

  const handleTypeCuisineCheckboxChange = useCallback(
    event =>
      updateCuisineTypeAndLocationTypeSameAsBrand(
        brandCurationTask.id,
        event.target.checked,
      ),
    [brandCurationTask.id, updateCuisineTypeAndLocationTypeSameAsBrand],
  )

  const onBulkUpdateLocations = useCallback(
    ({ mainLocationType, additionalLocationTypes, cuisineType, awards }) => {
      const data = {}
      if (mainLocationType) {
        data.mainLocationTypeId = mainLocationType
      }
      if (additionalLocationTypes) {
        data.additionalLocationTypeIds = additionalLocationTypes
      }
      if (awards) {
        data.awardIds = awards
      }
      if (cuisineType) {
        data.cuisineTypeId = cuisineType
      }
      bulkUpdateLocations(selectedItemIds, data, unselectAllItemIds)
    },
    [bulkUpdateLocations, selectedItemIds, unselectAllItemIds],
  )

  const previousLocationCuisineAndType = usePrevious(
    brandCurationTask.areLocationCuisineTypeAndLocationTypeSameAsBrand,
  )

  const previousBrandMainLocationType = usePrevious(
    brandCurationTask.brand?.mainLocationType?.id,
  )

  const previousBrandAdditionalLocationTypes = usePrevious(
    map(brandCurationTask.brand?.additionalLocationTypes, 'id'),
  )

  const previousBrandCuisineType = usePrevious(
    brandCurationTask.brand?.cuisineType?.id,
  )
  useEffect(() => {
    if (
      previousLocationCuisineAndType !==
        brandCurationTask.areLocationCuisineTypeAndLocationTypeSameAsBrand ||
      !areSameValue(
        previousBrandMainLocationType,
        brandCurationTask.brand?.mainLocationType?.id,
      ) ||
      !areSameValue(
        previousBrandCuisineType,
        brandCurationTask.brand?.cuisineType?.id,
      ) ||
      !isEmpty(
        differenceWith(
          previousBrandAdditionalLocationTypes,
          map(brandCurationTask.brand?.additionalLocationTypes, 'id'),
          (prev, next) => areSameValue(prev, next),
        ),
      )
    ) {
      refetchLocations(filters, pagination)
    }
  }, [
    brandCurationTask.areLocationCuisineTypeAndLocationTypeSameAsBrand,
    brandCurationTask.brand?.additionalLocationTypes,
    brandCurationTask.brand?.cuisineType?.id,
    brandCurationTask.brand?.mainLocationType?.id,
    filters,
    pagination,
    previousBrandAdditionalLocationTypes,
    previousBrandCuisineType,
    previousBrandMainLocationType,
    previousLocationCuisineAndType,
    refetchLocations,
    result,
  ])

  useHotkeys(
    join(
      [
        COMMON_AVAILABLE_HOTKEYS.CMD_RIGHT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.CTRL_RIGHT.hotkey,
      ],
      ',',
    ),
    event => {
      if (!paginationConfig) {
        return
      }

      event.preventDefault()
      const nextPage = paginationConfig.current + 1
      if (
        nextPage > Math.ceil(paginationConfig.total / paginationConfig.pageSize)
      ) {
        return
      }

      handleChangePagination(nextPage, pageSize)
    },
    [paginationConfig, handleChangePagination, pageSize],
  )

  useHotkeys(
    join(
      [
        COMMON_AVAILABLE_HOTKEYS.CMD_LEFT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.CTRL_LEFT.hotkey,
      ],
      ',',
    ),
    event => {
      if (!paginationConfig) {
        return
      }

      event.preventDefault()
      const prevPage = paginationConfig.current - 1
      if (prevPage < 1) {
        return
      }

      handleChangePagination(prevPage, pageSize)
    },
    [paginationConfig, handleChangePagination, pageSize],
  )

  return (
    <>
      <Row gutter={16}>
        <GridFormItem span={6} label="Locations">
          {total}
        </GridFormItem>

        <GridFormItem span={6} label="Locations have same Type & Cuisine">
          <Checkbox
            checked={
              brandCurationTask.areLocationCuisineTypeAndLocationTypeSameAsBrand
            }
            onChange={handleTypeCuisineCheckboxChange}
            disabled={isDisabled}
          />
        </GridFormItem>

        <GridFormItem span={6} label="Use brand name as location name">
          <Checkbox
            checked={brandCurationTask.brand?.useBrandNameAsLocationName}
            disabled
          />
        </GridFormItem>

        <StyledHotkeyContainer span={12}>
          <LocationsHotkeysLegend />
        </StyledHotkeyContainer>
      </Row>
      <StyledLocationsTable
        loading={isLoading}
        components={tableComponents}
        rowKey="id"
        className="locations-table"
        columns={tableColumns}
        dataSource={result}
        pagination={false}
      />

      <LocationsTableFooter
        isEditDisabled={isEditDisabled}
        mainLocationTypeOptions={mainLocationTypeOptions}
        additionalLocationTypeOptions={additionalLocationTypeOptions}
        awardOptions={awardOptions}
        cuisineTypeOptions={cuisineTypeOptions}
        onBulkUpdateLocations={onBulkUpdateLocations}
        paginationConfig={paginationConfig}
      />
    </>
  )
}

LocationsTable.propTypes = {
  brandCurationTask: brandCurationTaskPropType.isRequired,
  isDisabled: PropTypes.bool,
}

LocationsTable.defaultProps = {
  isDisabled: false,
}

export default LocationsTable
